import React from 'react';

import { Box, Collapse, Paper, Stack, Typography } from '@mui/material';

import { isRowApproved, isRowManuallyApproved } from '../../manualOverridePersistence';
import ChecklistEntityCardHeader from '../../reportCards/ChecklistEntityCardHeader';
import ManualChip from '../../reportCards/ManualChip';
import { statusRowSx } from '../../reportCards/statusRowSx';
import type { ManualOverrideState } from '../../types';

import RoomDealOptionRow from './RoomDealOptionRow';
import {
  DEAL_OPTION_ALLOCATION_COLUMN,
  DEAL_OPTION_NAME_COLUMN,
  DEAL_OPTION_OCCUPANCY_COLUMN,
  ROOM_CARD_GRID_TEMPLATE_COLUMNS,
} from './roomCardLayout';
import type { RoomRowView } from './types';

const DEAL_OPTION_HEADER_COLUMNS = [
  { label: 'Deal option', gridColumn: DEAL_OPTION_NAME_COLUMN },
  { label: 'Allocation', gridColumn: DEAL_OPTION_ALLOCATION_COLUMN },
  { label: 'Occupancy', gridColumn: DEAL_OPTION_OCCUPANCY_COLUMN },
];

interface Props {
  expanded: boolean;
  manualOverrides: ManualOverrideState;
  room: RoomRowView;
  onApprovalChange: (rowId: string) => void;
  onToggleDetails: (rowId: string, defaultOpen?: boolean) => void;
  onUnapprovalChange: (rowId: string) => void;
}

export default function RoomCard({
  expanded,
  manualOverrides,
  room,
  onApprovalChange,
  onToggleDetails,
  onUnapprovalChange,
}: Props) {
  const approved = isRowApproved(room, manualOverrides);
  const facts = [
    ...room.details.map((detail) => ({ label: detail.shortLabel ?? detail.label, value: detail.value })),
    ...room.capacity.map((entry) => ({ ...entry, label: `${entry.label} capacity` })),
  ];
  const dealOptionCount = room.dealOptions.length;

  return (
    <Paper variant="outlined" sx={{ borderRadius: 1.25, overflow: 'hidden' }}>
      <ChecklistEntityCardHeader
        approved={approved}
        cells={facts}
        countLabel={`${dealOptionCount} deal option${dealOptionCount === 1 ? '' : 's'}`}
        expanded={expanded}
        gridTemplateColumns={ROOM_CARD_GRID_TEMPLATE_COLUMNS}
        statusApproved={approved}
        title={room.name}
        titleAdornment={isRowManuallyApproved(room, manualOverrides) && <ManualChip />}
        onApprove={() => onApprovalChange(room.id)}
        onToggle={() => onToggleDetails(room.id, true)}
        onUnapprove={() => onUnapprovalChange(room.id)}
      />

      <Collapse in={expanded}>
        {room.expandedDetails.length > 0 && (
          <Stack spacing={1.5} sx={{ borderTop: 1, borderColor: 'divider', px: 2, py: 1.5 }}>
            {room.expandedDetails.map((detail) => (
              <Box key={detail.label}>
                <Typography
                  variant="caption"
                  fontWeight={700}
                  color="text.secondary"
                  sx={{ letterSpacing: '0.06em', display: 'block', mb: 0.5, textTransform: 'uppercase' }}
                >
                  {detail.label}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {detail.value}
                </Typography>
              </Box>
            ))}
          </Stack>
        )}
        <Box
          sx={{
            ...statusRowSx(),
            bgcolor: 'grey.50',
            borderTop: 1,
            borderTopColor: 'divider',
            columnGap: 2,
            display: 'grid',
            gridTemplateColumns: ROOM_CARD_GRID_TEMPLATE_COLUMNS,
            pl: 1.5,
            pr: 2,
            py: 1,
          }}
        >
          {DEAL_OPTION_HEADER_COLUMNS.map((column) => (
            <Typography
              key={column.label}
              variant="caption"
              color="text.secondary"
              fontWeight={700}
              sx={{ gridColumn: column.gridColumn, letterSpacing: 0.6, textTransform: 'uppercase' }}
            >
              {column.label}
            </Typography>
          ))}
        </Box>
        {room.dealOptions.map((dealOption) => (
          <RoomDealOptionRow key={dealOption.dealId} dealOption={dealOption} roomApproved={approved} />
        ))}
      </Collapse>
    </Paper>
  );
}
